import React, { useState, useEffect, useMemo } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaImage, FaCertificate, FaGraduationCap, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/apiClient';
import { Modal, Row, Col, Button, Spinner } from 'react-bootstrap';
import { CertificatePreviewFrame } from '../../../common/components/certificate/CertificateCanvas';
import { SAMPLE_CERTIFICATE_DATA } from '../../../common/utils/certificateLayout';

// Utility function to get the base URL for static files
const getStaticFileUrl = (filePath) => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
  const serverUrl = baseUrl.replace('/api', '');
  return `${serverUrl}/${filePath}`;
};

const CertificateTemplatesPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [serverPreviewUrl, setServerPreviewUrl] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    background_image: null,
    assigned_to_org: '',
    layout_config: {
      title: { x: 400, y: 150, fontSize: 36, color: '#000000' },
      learner_name: { x: 400, y: 250, fontSize: 28, color: '#000000' },
      course_name: { x: 400, y: 320, fontSize: 20, color: '#000000' },
      completion_date: { x: 400, y: 380, fontSize: 16, color: '#000000' },
      score: { x: 400, y: 420, fontSize: 16, color: '#000000' }
    }
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [organizationAdmins, setOrganizationAdmins] = useState([]);

  useEffect(() => {
    fetchTemplates();
    fetchOrganizations();
  }, [currentPage, searchTerm]);

  const summary = useMemo(() => {
    const total = templates.length;
    const active = templates.filter(template => parseInt(template.status, 10) === 1).length;
    const withAssignments = templates.filter(template => (template.courseAssignments?.length || 0) > 0).length;
    return { total, active, withAssignments };
  }, [templates]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.certificates.getTemplates({
        page: currentPage,
        limit: 10,
        search: searchTerm
      });

      if (response.data.success) {
        setTemplates(response.data.data.templates);
        setTotalPages(response.data.data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to fetch certificate templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await api.organizations.getAllOrganizations();
      let organizations = [];
      if (Array.isArray(response.data)) {
        organizations = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        organizations = response.data.data;
      } else if (response.data && response.data.organizations) {
        organizations = response.data.organizations;
      }
      setOrganizationAdmins(organizations);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to fetch organizations');
      setOrganizationAdmins([]);
    }
  };

  const handleShowModal = (mode, template = null) => {
    setModalMode(mode);
    setSelectedTemplate(template);

    if (mode === 'edit' && template) {
      let layoutConfig = template.layout_config;
      if (typeof template.layout_config === 'string') {
        try {
          layoutConfig = JSON.parse(template.layout_config);
        } catch (error) {
          console.error('Failed to parse layout_config:', error);
          layoutConfig = {
            title: { x: 400, y: 150, fontSize: 36, color: '#000000' },
            learner_name: { x: 400, y: 250, fontSize: 28, color: '#000000' },
            course_name: { x: 400, y: 320, fontSize: 20, color: '#000000' },
            completion_date: { x: 400, y: 380, fontSize: 16, color: '#000000' },
            score: { x: 400, y: 420, fontSize: 16, color: '#000000' }
          };
        }
      } else if (!layoutConfig) {
        layoutConfig = {
          title: { x: 400, y: 150, fontSize: 36, color: '#000000' },
          learner_name: { x: 400, y: 250, fontSize: 28, color: '#000000' },
          course_name: { x: 400, y: 320, fontSize: 20, color: '#000000' },
          completion_date: { x: 400, y: 380, fontSize: 16, color: '#000000' },
          score: { x: 400, y: 420, fontSize: 16, color: '#000000' }
        };
      }

      setFormData({
        name: template.name,
        description: template.description || '',
        background_image: null,
        assigned_to_org: template.assigned_to_org || '',
        layout_config: layoutConfig
      });
    } else {
      setFormData({
        name: '',
        description: '',
        background_image: null,
        assigned_to_org: '',
        layout_config: {
          title: { x: 400, y: 150, fontSize: 36, color: '#000000' },
          learner_name: { x: 400, y: 250, fontSize: 28, color: '#000000' },
          course_name: { x: 400, y: 320, fontSize: 20, color: '#000000' },
          completion_date: { x: 400, y: 380, fontSize: 16, color: '#000000' },
          score: { x: 400, y: 420, fontSize: 16, color: '#000000' }
        }
      });
    }

    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTemplate(null);
    setFormData({
      name: '',
      description: '',
      background_image: null,
      assigned_to_org: '',
      layout_config: {
        title: { x: 400, y: 150, fontSize: 36, color: '#000000' },
        learner_name: { x: 400, y: 250, fontSize: 28, color: '#000000' },
        course_name: { x: 400, y: 320, fontSize: 20, color: '#000000' },
        completion_date: { x: 400, y: 380, fontSize: 16, color: '#000000' },
        score: { x: 400, y: 420, fontSize: 16, color: '#000000' }
      }
    });
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'background_image') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleLayoutConfigChange = (section, property, value) => {
    setFormData(prev => ({
      ...prev,
      layout_config: {
        ...prev.layout_config,
        [section]: {
          ...prev.layout_config[section],
          [property]: property === 'fontSize' || property === 'x' || property === 'y'
            ? parseInt(value) || 0
            : value
        }
      }
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Template name is required';
    }

    if (modalMode === 'create' && !formData.background_image) {
      errors.background_image = 'Background image is required for new templates';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('description', formData.description);
      submitData.append('layout_config', JSON.stringify(formData.layout_config));
      
      if (formData.assigned_to_org) {
        submitData.append('assigned_to_org', formData.assigned_to_org);
      }

      if (formData.background_image) {
        submitData.append('background_image', formData.background_image);
      }

      let response;
      if (modalMode === 'create') {
        response = await api.certificates.createTemplate(submitData);
      } else {
        response = await api.certificates.updateTemplate(selectedTemplate.id, submitData);
      }

      if (response.data.success) {
        toast.success(`Template ${modalMode === 'create' ? 'created' : 'updated'} successfully`);
        handleCloseModal();
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(`Failed to ${modalMode} template`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    setDeleteLoading(true);
    try {
      const response = await api.certificates.deleteTemplate(templateToDelete.id);
      if (response.data.success) {
        toast.success('Template deleted successfully');
        setShowDeleteModal(false);
        setTemplateToDelete(null);
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to delete template');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
    setDeleteLoading(false);
  };

  const handleClosePreviewModal = () => {
    if (serverPreviewUrl) {
      URL.revokeObjectURL(serverPreviewUrl);
    }
    setServerPreviewUrl(null);
    setPreviewData(null);
    setShowPreviewModal(false);
  };

  const handlePreview = async (template) => {
    try {
      setPreviewLoading(true);
      const response = await api.certificates.renderImage({
        template_id: template.id,
        sample_data: SAMPLE_CERTIFICATE_DATA
      });
      const url = URL.createObjectURL(response.data);
      setServerPreviewUrl(url);
      setPreviewData(null);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleLocalPreview = async () => {
    try {
      setPreviewLoading(true);

      if (formData.background_image) {
        setPreviewData({
          backgroundImageUrl: URL.createObjectURL(formData.background_image),
          layoutConfig: formData.layout_config,
          data: SAMPLE_CERTIFICATE_DATA
        });
        setServerPreviewUrl(null);
        setShowPreviewModal(true);
        return;
      }

      const response = await api.certificates.renderImage({
        template_id: selectedTemplate?.id,
        layout_config: formData.layout_config,
        background_image: selectedTemplate?.background_image,
        sample_data: SAMPLE_CERTIFICATE_DATA
      });
      const url = URL.createObjectURL(response.data);
      setServerPreviewUrl(url);
      setPreviewData(null);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const renderLayoutConfigForm = () => {
    const sections = ['title', 'learner_name', 'course_name', 'completion_date', 'score'];

    return (
      <div className="mt-4">
        <h6 className="font-bold text-gray-800 mb-3">Layout Configuration</h6>
        <p className="text-xs text-gray-500 mb-3">
          Positions are relative to a fixed 800×600 canvas — the same size used in the downloaded PDF.
        </p>
        {sections.map(section => (
          <div key={section} className="bg-white rounded-xl shadow-sm border border-purple-100 p-4 mb-3">
            <small className="text-purple-600 font-medium capitalize">
              {section.replace('_', ' ')} Settings
            </small>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">X Position</label>
                <input
                  type="number"
                  value={formData.layout_config[section]?.x || 0}
                  onChange={(e) => handleLayoutConfigChange(section, 'x', e.target.value)}
                  className="w-full rounded-lg border-purple-200 focus:ring-purple-500 focus:border-purple-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Y Position</label>
                <input
                  type="number"
                  value={formData.layout_config[section]?.y || 0}
                  onChange={(e) => handleLayoutConfigChange(section, 'y', e.target.value)}
                  className="w-full rounded-lg border-purple-200 focus:ring-purple-500 focus:border-purple-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Font Size</label>
                <input
                  type="number"
                  value={formData.layout_config[section]?.fontSize || 16}
                  onChange={(e) => handleLayoutConfigChange(section, 'fontSize', e.target.value)}
                  className="w-full rounded-lg border-purple-200 focus:ring-purple-500 focus:border-purple-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
                <input
                  type="color"
                  value={formData.layout_config[section]?.color || '#000000'}
                  onChange={(e) => handleLayoutConfigChange(section, 'color', e.target.value)}
                  className="w-full h-10 rounded-lg cursor-pointer"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96 p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Certificate Templates
        </h5>
      </div>

    {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div className="bg-white rounded-xl shadow-md p-2 py-4 text-center">
          <p className="text-xs font-medium text-purple-600 uppercase tracking-wider">Total Templates</p>
          <p className="text-4xl font-bold text-purple-700 mt-2">{summary.total}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-2 py-4 text-center">
          <p className="text-xs font-medium text-green-600 uppercase tracking-wider">Active</p>
          <p className="text-4xl font-bold text-green-700 mt-2">{summary.active}</p>
        </div>
        {/* <div className="bg-white rounded-xl shadow-md p-2 py-4 text-center">
          <p className="text-xs font-medium text-blue-600 uppercase tracking-wider">Assigned to Courses</p>
          <p className="text-4xl font-bold text-blue-700 mt-2">{summary.withAssignments}</p>
        </div> */}
      </div>
      {/* Search + Add Button */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="md:col-span-2">
          <div className="flex shadow-sm rounded-lg sm:rounded-xl overflow-hidden bg-white">
            <span className="inline-flex items-center px-3 text-purple-600 bg-white border-r-0">
              <FaSearch className="w-4 h-4" />
            </span>
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => {
                setCurrentPage(1);
                setSearchTerm(e.target.value);
              }}
              className="w-full px-3 py-2 border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
            />
          </div>
        </div>
        <div>
          <button
            onClick={() => handleShowModal('create')}
            className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 "
          >
            <FaPlus className="w-4 h-4" />
            Create Template
          </button>
        </div>
      </div>

  

      {/* Table */}
      {templates.length === 0 ? (
        <div className="text-center py-10 bg-white/80 backdrop-blur rounded-xl shadow-md border border-purple-100">
          <p className="text-purple-700 font-medium">
            {searchTerm ? 'No templates found matching your search.' : 'No certificate templates yet. Click "Create Template" to get started.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
          <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-2 px-3 text-left">Name</th>
                <th className="py-2 px-3 text-left">Description</th>
                <th className="py-2 px-3 text-left">Background</th>
                <th className="py-2 px-3 text-left">Status</th>
                <th className="py-2 px-3 text-left">Created By</th>
                <th className="py-2 px-3 text-left">Organization</th>
                <th className="py-2 px-3 text-left">Courses</th>
                <th className="py-2 px-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.id} className="border-b border-purple-100 hover:bg-purple-50/50 transition-all">
                  <td className="py-2 px-3 text-gray-800">{template.name}</td>
                  <td className="py-2 px-3 text-gray-600">
                    {template.description ? (
                      template.description.length > 60 
                        ? `${template.description.substring(0, 60)}...` 
                        : template.description
                    ) : (
                      <span className="text-gray-800">No description</span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {template.background_image ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <FaImage className="w-3 h-3" /> Image Set
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        No Image
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${template.status === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {template.status === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-gray-700">
                    {template.creator ? `${template.creator.first_name} ${template.creator.last_name}` : 'Unknown'}
                  </td>
                  <td className="py-2 px-3">
                    {template.organization ? (
                      <span className="font-medium text-purple-700">{template.organization.name}</span>
                    ) : (
                      <span className="text-gray-800">Global</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${template.courseAssignments?.length ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
                      {(template.courseAssignments?.length || 0)} Courses
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex justify-left gap-2">
                      <button bsPrefix="custom-hamburger" onClick={() => handlePreview(template)} title="Preview" className="border-purple-300 text-purple-600 hover:bg-purple-50 rounded p-1">
                        <FaEye className="w-5 h-5" />
                      </button>
                      <button bsPrefix="custom-hamburger" onClick={() => handleShowModal('edit', template)} title="Edit" className="p-1 rounded-lg  border-yellow-300 text-yellow-600 hover:bg-yellow-50">
                        <FaEdit className="w-5 h-5" />
                      </button>
                      <button bsPrefix="custom-hamburger" onClick={() => navigate(`/admin/certificates/assignments?templateId=${template.id}`)} title="Assign Courses" className="p-1 rounded-lg  border-blue-300 text-blue-600 hover:bg-blue-50">
                        <FaGraduationCap className="w-5 h-5" />
                      </button>
                      <button bsPrefix="custom-hamburger" onClick={() => handleDeleteClick(template)} title="Delete" className="p-1 rounded-lg  border-red-300 text-red-600 hover:bg-red-50">
                        <FaTrash className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered fullscreen="lg-down">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 px-4 py-3 sm:px-6 sm:py-4">
          <Modal.Title className="text-sm sm:text-base">{modalMode === 'create' ? 'Create Certificate Template' : 'Edit Certificate Template'}</Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-0">
          <form id="templateForm" onSubmit={handleSubmit} className="py-3 px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Template Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={` ${formErrors.name ? 'border-red-500' : 'border-purple-200'} rounded-lg border-purple-200 focus:ring-purple-500 form-control`}
                  placeholder="Enter template name"
                />
                {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Background Image {modalMode === 'create' && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="file"
                  name="background_image"
                  onChange={handleInputChange}
                  accept="image/*"
                  className={`w-full rounded-2xl border-2 border-dashed border-purple-400 ${formErrors.background_image ? 'border-red-500' : 'border-purple-200'} focus:ring-purple-500 text-sm file:mr-2 file:py-2 file:px-2 file:rounded-xl file:border-0 file:bg-gradient-to-r file:from-purple-600 file:to-pink-600 file:text-white file:font-bol`}
                />
                {formErrors.background_image && <p className="text-red-500 text-xs mt-1">{formErrors.background_image}</p>}
                <p className="text-xs text-gray-500 mt-1">Supported: JPG, PNG, GIF (Max 10MB)</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
              <textarea
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-purple-200 focus:ring-purple-500 focus:border-purple-500 text-sm"
                placeholder="Enter description (optional)"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Assign to Organization</label>
              <select
                name="assigned_to_org"
                value={formData.assigned_to_org}
                onChange={handleInputChange}
                className="rounded-lg border-purple-200 focus:ring-purple-500 form-select"
              >
                <option value="">Global (All Organizations)</option>
                {organizationAdmins.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Optional: restrict this template to a specific organization</p>
            </div>

            {renderLayoutConfigForm()}
          </form>
        </Modal.Body>

        <Modal.Footer className="border-0 p-3">
          <Row className="w-100 g-2">
            <Col xs={4}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleCloseModal}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col xs={4}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleLocalPreview}
                disabled={submitting}
                className="w-full border-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                <FaEye className="w-4 h-4" /> Preview
              </Button>
            </Col>
            <Col xs={4}>
              <Button
                bsPrefix="custom-hamburger"
                type="submit"
                form="templateForm"
                disabled={submitting}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:bg-purple-50 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  modalMode === 'create' ? 'Create Template' : 'Update Template'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Delete Certificate Template</h5>
          <p className="text-gray-600">
            Are you sure you want to delete{' '}
            <strong>{templateToDelete ? templateToDelete.name : ''}</strong>?
          </p>
          <p className="text-sm text-gray-500 mt-3">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleteLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Template'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Preview Modal */}
      <Modal show={showPreviewModal} onHide={handleClosePreviewModal} centered fullscreen="md-down">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 px-4 py-3 sm:px-6 sm:py-4">
          <Modal.Title className="text-sm sm:text-base">Certificate Preview (800 × 600)</Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-6 bg-gray-50">
          {previewLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-gray-600">Generating preview...</p>
            </div>
          ) : serverPreviewUrl ? (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden p-4">
              <div className="w-full flex justify-center">
                <img
                  src={serverPreviewUrl}
                  alt="Certificate preview"
                  style={{ width: 800, maxWidth: '100%', height: 'auto', display: 'block' }}
                />
              </div>
            </div>
          ) : previewData ? (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden p-4">
              <CertificatePreviewFrame
                backgroundImageUrl={previewData.backgroundImageUrl}
                layoutConfig={previewData.layoutConfig}
                data={previewData.data}
              />
            </div>
          ) : null}
          <p className="text-center text-muted small mt-3 mb-0">
            Preview matches the downloaded PDF exactly (800×600 canvas).
          </p>
        </Modal.Body>

        <Modal.Footer className="border-0 p-3">
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleClosePreviewModal}
            className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CertificateTemplatesPage;